import { eventCheck, idleCheck, loadCheck, mediaCheck, visibleCheck } from '../strategies';

/**
 * AlpineAdaptor
 *
 * Handles lazy-loading of Alpine.js block modules based on strategies
 * configured via `pegasus.lazy` in each block's block.json.
 *
 * Lifecycle (called from main.js):
 *   1. constructor(Alpine)        — stores Alpine reference
 *   2. initLazyBlocks(modules)    — scans DOM, marks lazy elements with x-ignore (before Alpine.start())
 *   3. observeLazyBlocks()        — begins strategy-based observation (after Alpine.start())
 */
export class AlpineAdaptor {
	Alpine = null;
	lazyElements = [];
	loadedModules = new Set();

	constructor(Alpine) {
		this.Alpine = Alpine;
	}

	/**
	 * Scan the DOM for [x-data] elements whose Alpine data name exists in the
	 * lazy modules map. Mark each with x-ignore so Alpine.start() skips them.
	 * Must be called BEFORE Alpine.start().
	 *
	 * @param {Object} blocks - The modules map from block-imports/modules.js
	 */
	initLazyBlocks(blocks) {
		if (!blocks || typeof blocks !== 'object') return;

		document.querySelectorAll('[x-data]').forEach((el) => {
			const dataName = el.getAttribute('x-data').split('(')[0].trim();
			const config = blocks[dataName];
			if (!config) return;

			const loader = typeof config === 'function' ? config : config?.loader;
			const strategy = typeof config === 'object' && config.strategy ? config.strategy : 'visible';
			if (!loader) return;

			el.setAttribute('x-ignore', '');
			this.lazyElements.push({ element: el, dataName, loader, strategy });
		});
	}

	/**
	 * Begin observing every lazy element with its configured loading strategy.
	 * Must be called AFTER Alpine.start().
	 */
	observeLazyBlocks() {
		for (const entry of this.lazyElements) {
			this.observeElement(entry);
		}
	}

	/**
	 * Apply a loading strategy to a single lazy element.
	 */
	async observeElement({ element, dataName, loader, strategy }) {
		if (strategy === 'idle') {
			await idleCheck();
		} else if (strategy === 'load') {
			loadCheck();
		} else if (strategy.startsWith('event:')) {
			await eventCheck({ event: strategy.substring(6) });
		} else if (strategy.startsWith('media:')) {
			await mediaCheck({ query: strategy.substring(6) });
		} else {
			// Default: visible (IntersectionObserver)
			await visibleCheck({ element });
		}

		await this.loadLazyModule(element, loader);
	}

	/**
	 * Load a lazy block module and activate it.
	 * Re-dispatches alpine:init so the module's own Alpine.data() calls run.
	 */
	async loadLazyModule(element, loader) {
		const dataName = element.getAttribute('x-data').split('(')[0].trim();

		if (!this.loadedModules.has(dataName)) {
			await loader();
			document.dispatchEvent(new CustomEvent('alpine:init'));
			this.loadedModules.add(dataName);
		}

		this.activate(element);
	}

	/**
	 * Activate a lazy element: remove x-ignore and let Alpine initialise it.
	 */
	activate(element) {
		if (!element.isConnected) return;

		this.Alpine.destroyTree(element);

		element.removeAttribute('x-ignore');
		element._x_ignore = false;

		if (this.hasIgnoredParent(element)) return;

		this.Alpine.initTree(element);
	}

	/**
	 * Walk up the DOM to check if any ancestor has x-ignore.
	 */
	hasIgnoredParent(element) {
		let node = element?.parentElement;
		while (node && node.nodeName !== 'HTML') {
			if (node.hasAttribute('x-ignore')) return true;
			node = node.parentElement;
		}
		return false;
	}
}
